import { usePage } from '@inertiajs/react';

import type { SharedProps } from '@/Types/inertia';

/**
 * Per-feature translation dict shape. Populated by HandleInertiaRequests
 * from lang/{locale}/{feature}.php. Adding a new feature group? Update
 * TRANSLATION_GROUPS in HandleInertiaRequests AND extend this type.
 */
export type Messages = {
    common: Record<string, string>;
    nav: Record<string, string>;
    home: Record<string, string>;
    search: Record<string, string>;
    lawyer: Record<string, string>;
    get_matched: Record<string, string>;
    portal: Record<string, string>;
    admin: Record<string, string>;
};

type Group = keyof Messages;

/**
 * useT() — feature-scoped translation hook.
 *
 *   const t = useT('nav');
 *   t('home') // → 'Home' (en) or 'Inicio' (es)
 *
 * Falls back to the key itself when missing so untranslated strings are
 * visible (and grep-able) rather than silently empty.
 *
 * Replacements: pass an object as the second arg.
 *   t('review_count', { count: 12 })
 */
export function useT<G extends Group>(
    group: G,
): (key: string, replacements?: Record<string, string | number>) => string {
    const { messages } = usePage<SharedProps>().props;
    const bag = messages[group] ?? {};

    return (key, replacements) => {
        let value = bag[key];
        if (typeof value !== 'string' || value === '') return key;

        if (replacements) {
            for (const [token, repl] of Object.entries(replacements)) {
                value = value.split('{' + token + '}').join(String(repl));
            }
        }
        return value;
    };
}

/**
 * useLocale() — current locale + switcher.
 *
 *   const { locale, available, switchTo } = useLocale();
 *   switchTo('es') // adds ?lang=es to the current URL and full-page-reloads.
 *
 * The full reload is intentional: SetLocale middleware writes the
 * cookie + Inertia re-fetches the entire page including messages, which
 * is what users expect from a language switch. SPA-style swap would
 * leave server-rendered SEO meta-tags in the old locale.
 */
export function useLocale(): {
    locale: string;
    available: string[];
    switchTo: (target: string) => void;
} {
    const { locale, availableLocales } = usePage<SharedProps>().props;

    return {
        locale,
        available: availableLocales,
        switchTo: (target) => {
            if (typeof window === 'undefined') return;
            const url = new URL(window.location.href);
            url.searchParams.set('lang', target);
            window.location.assign(url.toString());
        },
    };
}
